import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';

export type ShippingElementResponseArgs = {
  id: number;

  // サーバ側でリストを動的作成しているので、フロントでは自由度を持たせておく
  category?: string; // 'echoai' | 'letter_pack' | 'sagawa' | 'yamato'
  category_text?: string; // 'エコハイ' | 'レターパック' | '佐川急便' | 'ヤマト運輸'

  remark?: string;
  shipped_at?: string;
  arrived_at?: string;
  number?: string;
};

export type ShippingElementArgs = {
  id: number;
  category?: string;
  categoryText?: string;
  shippingCategoryOptions?: { [key: string]: string };
  remark?: string;
  shippedAt?: Moment;
  arrivedAt?: Moment;
  number?: string;
};

export const convertShippingElementResponseToArgs = (
  response: ShippingElementResponseArgs,
  shippingCategoryOptions?: { [key: string]: string }
) => {
  const args: ShippingElementArgs = {
    id: response.id,
    category: response.category,
    categoryText: response.category_text,
    shippingCategoryOptions,
    remark: response.remark,
    shippedAt: response.shipped_at ? moment(response.shipped_at) : undefined,
    arrivedAt: response.arrived_at ? moment(response.arrived_at) : undefined,
    number: response.number
  };
  return args;
};

export default class ShippingElement {
  @observable id: number;
  @observable category?: string;
  @observable categoryText?: string;
  @observable remark?: string;
  @observable shippedAt?: Moment;
  @observable arrivedAt?: Moment;
  @observable number?: string;
  shippingCategoryOptions?: { [key: string]: string };

  constructor(args: ShippingElementArgs) {
    this.id = args.id;
    this.category = args.category;
    this.shippingCategoryOptions = args.shippingCategoryOptions;
    this.categoryText = args.categoryText ?? this.resolveCategoryText(args.category);
    this.number = args.number || '';
    this.remark = args.remark;
    const defaultStartedAt = args.shippedAt ?? moment();
    this.shippedAt = defaultStartedAt;
    this.arrivedAt = args.arrivedAt ?? defaultStartedAt.clone().add(1, 'day');

    if ((!this.category || this.category === '') && this.shippingCategoryOptions) {
      const [defaultKey] = Object.keys(this.shippingCategoryOptions);
      if (defaultKey !== undefined) {
        this.category = defaultKey;
      }
    }
  }

  get type() {
    return 'shipping';
  }

  get title() {
    return '配送料';
  }

  get summary() {
    return `[配送料]${this.categoryLabel}`;
  }

  get name() {
    return this.title;
  }

  detail() {
    return '';
  }

  validationErrors(_orderItemStatus?: number): string[] {
    const errors: string[] = [];

    if (!this.remark) {
      errors.push('配送料の備考を入力してください。');
    }
    if (this.arrivedAt && this.shippedAt && this.arrivedAt.isBefore(this.shippedAt)) {
      errors.push('配送日時以降を指定してください。');
    }

    return errors;
  }

  get description(): string {
    return `\
<配送料>
■種別：${this.categoryLabel}
■発送日時：${this.shippedAt?.format('MM/DD HH:mm')}
■到着日時：${this.arrivedAt?.format('MM/DD HH:mm')}
■特定記録番号：${this.number}
■備考
${this.remark}\
`;
  }

  get updateParams() {
    return {
      type: this.type,
      category: this.category,
      remark: this.remark,
      shipped_at: this.shippedAt?.format('YYYY-MM-DD HH:mm:ss'),
      arrived_at: this.arrivedAt?.format('YYYY-MM-DD HH:mm:ss'),
      number: this.number
    };
  }

  get startTime() {
    return this.arrivedAt;
  }

  get endTime() {
    return this.arrivedAt;
  }

  get categoryLabel(): string {
    return this.categoryText ?? this.resolveCategoryText(this.category) ?? this.category ?? '';
  }

  private resolveCategoryText(category: string | undefined): string | undefined {
    if (!category) return undefined;
    return this.shippingCategoryOptions?.[category];
  }
}
